import { useEffect, useState } from 'react';
import managerService, { type ManagerAgent } from '../../services/manager.service';
import schedulingService, { type AppointmentSummary } from '../../services/scheduling.service';
import { getApiErrorMessage } from '../../lib/apiError';
import { fullName } from '../../lib/fullName';
import { colors } from '../../styles/theme';
import Modal from '../Modal';
import { readerLocale } from '../../i18n/language';

interface Props {
  appointment: AppointmentSummary;
  onClose: () => void;
  onReassigned: (updated: AppointmentSummary) => void;
}

const MIN_REASON = 5;
const MAX_REASON = 500;

/**
 * Manager-only modal for moving a single appointment to another agent.
 * The case ownership stays with the original agent — this is a logistics
 * decision, not a relationship change. See memory/scheduling_decisions
 * point 4.
 */
export default function ReassignAppointmentModal({
  appointment,
  onClose,
  onReassigned,
}: Readonly<Props>): JSX.Element {
  const [agents, setAgents] = useState<ManagerAgent[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    managerService.listAgents()
      .then((data) => { if (!cancelled) setAgents(data); })
      .catch((err: unknown) => {
        if (!cancelled) setLoadError(getApiErrorMessage(err, 'Could not load agents.'));
      });
    return () => { cancelled = true; };
  }, []);

  async function handleSubmit(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setSubmitError(null);
    if (!selectedId) {
      setSubmitError('Please select an agent.');
      return;
    }
    const trimmed = reason.trim();
    if (trimmed.length < MIN_REASON) {
      setSubmitError(`Reason must be at least ${MIN_REASON} characters.`);
      return;
    }
    setSubmitting(true);
    try {
      const updated = await schedulingService.reassignAppointment(appointment.id, {
        new_agent_id: selectedId,
        reason: trimmed,
      });
      onReassigned(updated);
    } catch (err: unknown) {
      setSubmitError(getApiErrorMessage(err, 'Could not reassign this appointment.'));
    } finally {
      setSubmitting(false);
    }
  }

  const startsLabel = (() => {
    try {
      return new Date(appointment.starts_at).toLocaleString(readerLocale(), {
        weekday: 'short', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit',
      });
    } catch {
      return appointment.starts_at;
    }
  })();

  const currentAgentId = appointment.agent?.id ?? null;

  return (
    <Modal open onClose={onClose} busy={submitting} maxWidth="lg">
        <h2 className="text-lg font-semibold mb-1" style={{ color: colors.ink }}>
          Reassign appointment
        </h2>
        <p className="text-sm mb-4" style={{ color: colors.muted }}>
          Move this single call to another agent. Case ownership won&apos;t change.
        </p>

        <div className="rounded-lg p-3 mb-4 text-sm space-y-1"
             style={{ background: colors.canvas, border: `1px solid ${colors.borderSoft}` }}>
          <div><span style={{ color: colors.mutedSoft }}>Member:</span>{' '}
            <span style={{ color: colors.ink }}>{fullName(appointment.member)}</span></div>
          <div><span style={{ color: colors.mutedSoft }}>When:</span>{' '}
            <span style={{ color: colors.ink }}>{startsLabel}</span></div>
          <div><span style={{ color: colors.mutedSoft }}>Currently with:</span>{' '}
            <span style={{ color: colors.ink }}>
              {appointment.agent ? fullName(appointment.agent) : <em style={{ color: colors.danger }}>Orphaned</em>}
            </span></div>
        </div>

        {loadError && (
          <div className="rounded-lg p-3 text-sm mb-3" role="alert"
               style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
            {loadError}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <label className="block text-sm">
            <span className="block mb-1 font-medium" style={{ color: colors.ink }}>New agent</span>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              disabled={agents === null || submitting}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ border: `1px solid ${colors.border}`, color: colors.ink, background: colors.surface }}
            >
              <option value="">{agents === null ? 'Loading…' : 'Select an agent'}</option>
              {agents
                ? [...agents]
                    .sort((a, b) => fullName(a).localeCompare(fullName(b)))
                    .map((a) => {
                      const isCurrent = a.id === currentAgentId;
                      return (
                        <option key={a.id} value={a.id} disabled={isCurrent}>
                          {fullName(a)} · {a.pending_count} pending{isCurrent ? ' (current)' : ''}
                        </option>
                      );
                    })
                : null}
            </select>
          </label>

          <label className="block text-sm">
            <span className="block mb-1 font-medium" style={{ color: colors.ink }}>
              Reason{' '}
              <span className="font-normal" style={{ color: colors.mutedSoft }}>
                ({MIN_REASON}–{MAX_REASON} chars; written to the audit log)
              </span>
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              minLength={MIN_REASON}
              maxLength={MAX_REASON}
              className="w-full px-3 py-2 rounded-lg text-sm"
              style={{ border: `1px solid ${colors.border}`, color: colors.ink }}
              placeholder="Workload rebalance, agent unreachable, …"
            />
          </label>

          {submitError && (
            <div className="rounded-lg p-3 text-sm" role="alert"
                 style={{ background: colors.dangerBg, color: colors.danger, border: `1px solid ${colors.dangerBorder}` }}>
              {submitError}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} disabled={submitting}
                    className="px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    style={{ background: colors.surface, color: colors.inkSoft, border: `1px solid ${colors.border}` }}>
              Cancel
            </button>
            <button type="submit" disabled={submitting || agents === null}
                    className="px-4 py-2 rounded-lg text-sm font-semibold transition-colors"
                    style={{
                      background: colors.navy, color: '#FFFFFF',
                      opacity: submitting ? 0.6 : 1,
                      cursor: submitting ? 'wait' : 'pointer',
                    }}>
              {submitting ? 'Reassigning…' : 'Reassign appointment'}
            </button>
          </div>
        </form>
    </Modal>
  );
}
